import React, { useEffect, useMemo, useState } from 'react';
import {
  CreditCard,
  User,
  Phone,
  Mail,
  MessageSquare,
  ShoppingBag,
  Store,
  Truck,
  Calendar,
  Clock,
  MapPin,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCart } from '@/context/CartContext';
import { businessInfo } from '@/data/menuData';
import { toast } from 'sonner';
import { formatPrice } from '@/lib/formatPrice';

const DEFAULT_WHATSAPP_NUMBER = '393512649502';
const STRIPE_CHECKOUT_URL = `${window.location.origin}/menu-digitale/api/stripe/create-checkout-session.php`;
const ORDER_CREATE_URL = `${window.location.origin}/menu-digitale/api/orders/index.php`;

const normalizeWhatsappNumber = (value) => {
  if (!value) return '';

  let digits = String(value).replace(/\D+/g, '');

  if (digits.startsWith('0039')) digits = digits.slice(2);
  if (digits.length === 10 && !digits.startsWith('39')) digits = `39${digits}`;

  return digits;
};

const buildWhatsAppMessage = ({
  items,
  fulfillmentType,
  deliveryAddress,
  deliveryFee,
  deliveryDistance,
  pickupDate,
  pickupTime,
  customerInfo,
  subtotal,
  total,
  orderId
}) => {
  const lines = [];

  lines.push('🧾 *NUOVO ORDINE SAPORI 1989*');
  if (orderId) lines.push(`📌 Ordine #${orderId}`);
  lines.push('');

  lines.push(`👤 *Cliente:* ${customerInfo.name || '-'}`);
  lines.push(`📞 *Telefono:* ${customerInfo.phone || '-'}`);
  lines.push(`📧 *Email:* ${customerInfo.email || '-'}`);
  lines.push('');

  if (fulfillmentType === 'PICKUP') {
    lines.push('🏪 *Modalità:* Ritiro in sede');
    if (pickupDate) lines.push(`📅 *Data ritiro:* ${pickupDate}`);
    if (pickupTime) lines.push(`⏰ *Ora ritiro:* ${pickupTime}`);
    lines.push('💳 *Pagamento:* In sede');
  } else {
    lines.push('🚚 *Modalità:* Consegna a domicilio');
    if (deliveryAddress) lines.push(`📍 *Indirizzo:* ${deliveryAddress}`);
    if (deliveryDistance !== null && deliveryDistance !== undefined) {
      lines.push(`🛣️ *Distanza:* ${Number(deliveryDistance).toFixed(1)} km`);
    }
    lines.push(`💶 *Consegna:* ${formatPrice(deliveryFee || 0)}`);
    lines.push('💳 *Pagamento:* Online con carta');
  }

  lines.push('');
  lines.push('🍽️ *Dettaglio ordine:*');

  items.forEach((item) => {
    const gramsText = item.grams ? ` (${item.grams}g)` : '';
    const senzaLattosioText = item.senza_lattosio ? ' - SENZA LATTOSIO: SI' : ' - SENZA LATTOSIO: NO';
    lines.push(
      `- ${item.quantity}x ${item.name}${gramsText}${senzaLattosioText} → ${formatPrice(item.lineTotal)}`
    );
  });

  lines.push('');
  lines.push(`💰 *Subtotale:* ${formatPrice(subtotal)}`);
  lines.push(
    fulfillmentType === 'PICKUP'
      ? '💰 *Ritiro:* Gratuito'
      : `💰 *Consegna:* ${formatPrice(deliveryFee || 0)}`
  );
  lines.push(`✅ *Totale:* ${formatPrice(total)}`);

  if (customerInfo.notes?.trim()) {
    lines.push('');
    lines.push(`📝 *Note:* ${customerInfo.notes.trim()}`);
  }

  lines.push('');
  lines.push('Inviato dal Menu Digitale Sapori 1989');

  return lines.join('\n');
};

export const CheckoutSection = () => {
  const {
    items,
    subtotal,
    total,
    fulfillmentType,
    deliveryAddress,
    deliveryFee,
    deliveryDistance,
    deliveryUnavailable,
    isCheckoutBlocked,
    pickupDate,
    pickupTime,
    customerInfo,
    setCustomerInfo,
    isEmpty,
    clearCart
  } = useCart();

  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [whatsappNumber, setWhatsappNumber] = useState(DEFAULT_WHATSAPP_NUMBER);
  const [loadingWhatsappNumber, setLoadingWhatsappNumber] = useState(true);

  useEffect(() => {
    const fetchPublicSettings = async () => {
      try {
        const response = await fetch(
          'https://www.sapori1989.it/menu-digitale/api/public/settings.php',
          {
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          throw new Error('Impostazioni pubbliche non disponibili');
        }

        const data = await response.json();
        const numberFromApi =
          data?.settings?.whatsapp_number ||
          data?.whatsapp_number ||
          '';

        const normalized = normalizeWhatsappNumber(numberFromApi);

        if (normalized) {
          setWhatsappNumber(normalized);
        } else {
          setWhatsappNumber(DEFAULT_WHATSAPP_NUMBER);
        }
      } catch (error) {
        console.warn('[Checkout] Uso numero WhatsApp di backup:', error);
        setWhatsappNumber(DEFAULT_WHATSAPP_NUMBER);
      } finally {
        setLoadingWhatsappNumber(false);
      }
    };

    fetchPublicSettings();
  }, []);

  const effectiveWhatsappNumber = useMemo(() => {
    const normalized = normalizeWhatsappNumber(whatsappNumber);
    return normalized || DEFAULT_WHATSAPP_NUMBER;
  }, [whatsappNumber]);

  const customerWhatsappNumber = useMemo(() => {
    return normalizeWhatsappNumber(customerInfo.phone);
  }, [customerInfo.phone]);

  const validateForm = () => {
    const newErrors = {};

    if (!customerInfo.name || customerInfo.name.trim().length < 2) {
      newErrors.name = 'Inserisci il tuo nome';
    }

    const cleanedPhone = (customerInfo.phone || '').replace(/\D/g, '');
    if (!cleanedPhone || !/^(39)?[0-9]{10}$/.test(cleanedPhone)) {
      newErrors.phone = 'Inserisci un numero di telefono valido';
    }

    if (!customerInfo.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = "Inserisci un'email valida";
    }

    if (fulfillmentType === 'PICKUP') {
      if (!pickupDate) newErrors.pickupDate = 'Seleziona una data per il ritiro';
      if (!pickupTime) newErrors.pickupTime = 'Seleziona un orario per il ritiro';
    }

    if (fulfillmentType === 'DELIVERY') {
      if (!deliveryAddress || deliveryAddress.trim().length < 5) {
        newErrors.deliveryAddress = 'Inserisci un indirizzo di consegna valido';
      }
      if (deliveryDistance === null) {
        newErrors.deliveryAddress = 'Calcola prima la distanza cliccando su "Calcola"';
      }
      if (deliveryUnavailable) {
        newErrors.deliveryAddress = 'La consegna non è disponibile per questa zona (oltre 50 km)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildOrderPayload = () => ({
    customer_name: customerInfo.name,
    customer_phone: (customerInfo.phone || '').replace(/\D/g, ''),
    customer_email: customerInfo.email,
    customer_notes: customerInfo.notes || '',
    fulfillment_type: fulfillmentType,
    pickup_date: fulfillmentType === 'PICKUP' ? pickupDate : null,
    pickup_time: fulfillmentType === 'PICKUP' ? pickupTime : null,
    delivery_address: fulfillmentType === 'DELIVERY' ? deliveryAddress : null,
    delivery_distance_km: fulfillmentType === 'DELIVERY' ? deliveryDistance : null,
    delivery_fee: fulfillmentType === 'DELIVERY' ? deliveryFee : 0,
    subtotal,
    total,
    items: items.map((item) => ({
      id: item.id,
      name: item.name,
      grams: item.grams,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      line_total: item.lineTotal,
      senza_lattosio: item.senza_lattosio ? 1 : 0
    }))
  });

  const createOrderForPickup = async () => {
    const payload = buildOrderPayload();

    const response = await fetch(ORDER_CREATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (!response.ok) {
      throw new Error(
        data?.error ||
          data?.message ||
          'Errore nella creazione dell’ordine'
      );
    }

    return data;
  };

  const openWhatsAppCheckout = ({ orderId }) => {
    const message = buildWhatsAppMessage({
      items,
      fulfillmentType,
      deliveryAddress,
      deliveryFee,
      deliveryDistance,
      pickupDate,
      pickupTime,
      customerInfo,
      subtotal,
      total,
      orderId
    });

    const targetWhatsappNumber =
      fulfillmentType === 'PICKUP'
        ? customerWhatsappNumber
        : effectiveWhatsappNumber;

    if (!targetWhatsappNumber) {
      throw new Error('Numero WhatsApp non valido');
    }

    const url = `https://wa.me/${targetWhatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCheckout = async () => {
    if (isCheckoutBlocked) {
      toast.error('La consegna non è disponibile per la zona selezionata');
      return;
    }

    if (!validateForm()) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsProcessing(true);

    try {
      if (fulfillmentType === 'PICKUP') {
        if (!customerWhatsappNumber) {
          toast.error('Inserisci un numero WhatsApp cliente valido');
          return;
        }

        const createdOrder = await createOrderForPickup();
        const createdOrderId = createdOrder?.order_id || null;

        if (!createdOrderId) {
          throw new Error('Ordine creato senza ID valido');
        }

        openWhatsAppCheckout({ orderId: createdOrderId });
        toast.success('Ordine salvato e inviato! Ti apriamo WhatsApp sul numero del cliente.');
        clearCart();
        return;
      }

      const payload = {
        amount: Math.round(total * 100),
        customer_name: customerInfo.name,
        customer_phone: (customerInfo.phone || '').replace(/\D/g, ''),
        customer_email: customerInfo.email,
        customer_notes: customerInfo.notes || '',
        fulfillment_type: fulfillmentType,
        pickup_date: fulfillmentType === 'PICKUP' ? pickupDate : null,
        pickup_time: fulfillmentType === 'PICKUP' ? pickupTime : null,
        delivery_address: fulfillmentType === 'DELIVERY' ? deliveryAddress : null,
        delivery_distance_km: fulfillmentType === 'DELIVERY' ? deliveryDistance : null,
        delivery_fee: fulfillmentType === 'DELIVERY' ? deliveryFee : 0,
        subtotal,
        total,
        items: items.map((item) => ({
          id: item.id,
          name: item.name,
          grams: item.grams,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          line_total: item.lineTotal,
          senza_lattosio: item.senza_lattosio ? 1 : 0
        }))
      };

      const response = await fetch(STRIPE_CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Errore nella creazione del checkout Stripe');
      }

      if (!data.url) {
        throw new Error('URL checkout Stripe non ricevuto');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);

      if (fulfillmentType === 'PICKUP') {
        toast.error(error?.message || 'Errore durante il salvataggio dell’ordine.');
        return;
      }

      try {
        openWhatsAppCheckout({ orderId: null });
        toast.error('Checkout online non disponibile. Ti apriamo WhatsApp per completare l’ordine.');
      } catch {
        toast.error(error?.message || 'Errore durante il checkout. Riprova.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (isEmpty) return null;

  return (
    <section className="py-10 px-4" id="checkout">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Completa il tuo <span className="gradient-instagram-text">ordine</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            Inserisci i tuoi dati per procedere
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <h3 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-instagram-purple" />
              I tuoi dati
            </h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  Nome e Cognome *
                </Label>
                <Input
                  id="name"
                  placeholder="Mario Rossi"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  Telefono *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="3331234567"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  className={errors.phone ? 'border-destructive' : ''}
                />
                {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="mario.rossi@email.com"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  Note (opzionale)
                </Label>
                <Textarea
                  id="notes"
                  placeholder="Eventuali richieste speciali..."
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <ShoppingBag className="w-5 h-5 text-instagram-purple" />
              Riepilogo ordine
            </h3>

            <div className="mb-4">
              {fulfillmentType === 'PICKUP' ? (
                <div className="bg-instagram-purple/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Store className="w-5 h-5 text-instagram-purple" />
                    <span className="font-semibold text-foreground">Ritiro in Sede</span>
                    <Badge className="gradient-instagram text-primary-foreground ml-auto">Gratuito</Badge>
                  </div>

                  {pickupDate && pickupTime && (
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(pickupDate).toLocaleDateString('it-IT', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {pickupTime}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                    <MapPin className="w-3 h-3" />
                    {businessInfo.address}
                  </div>
                </div>
              ) : (
                <div className="bg-success/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-5 h-5 text-success" />
                    <span className="font-semibold text-foreground">Consegna a Domicilio</span>
                    <Badge className="bg-success text-success-foreground ml-auto">
                      {formatPrice(deliveryFee)}
                    </Badge>
                  </div>

                  {deliveryAddress && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      {deliveryAddress}
                    </div>
                  )}

                  {deliveryDistance && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Distanza: {deliveryDistance.toFixed(1)} km
                    </p>
                  )}
                </div>
              )}
            </div>

            {(errors.pickupDate || errors.pickupTime || errors.deliveryAddress) && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  {errors.pickupDate || errors.pickupTime || errors.deliveryAddress}
                </AlertDescription>
              </Alert>
            )}

            {!loadingWhatsappNumber && effectiveWhatsappNumber === DEFAULT_WHATSAPP_NUMBER && (
              <Alert className="mb-4">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Numero WhatsApp caricato in modalità backup.
                </AlertDescription>
              </Alert>
            )}

            <Separator className="my-4" />

            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {items.map((item, index) => (
                <div key={`${item.id}-${index}`} className="flex justify-between text-sm gap-3">
                  <div className="text-foreground">
                    <div>
                      {item.quantity}x {item.name} ({item.grams}g)
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Senza lattosio: {item.senza_lattosio ? 'Sì' : 'No'}
                    </div>
                  </div>
                  <span className="font-medium text-foreground whitespace-nowrap">
                    {formatPrice(item.lineTotal)}
                  </span>
                </div>
              ))}
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotale</span>
                <span className="text-foreground">{formatPrice(subtotal)}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {fulfillmentType === 'PICKUP' ? 'Ritiro' : 'Consegna'}
                </span>
                <span className={fulfillmentType === 'PICKUP' ? 'text-success' : 'text-foreground'}>
                  {fulfillmentType === 'PICKUP' ? 'Gratuito' : formatPrice(deliveryFee)}
                </span>
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Totale</span>
                <span className="text-2xl font-bold gradient-instagram-text">
                  {formatPrice(total)}
                </span>
              </div>
            </div>

            {isCheckoutBlocked ? (
              <div className="mt-6">
                <Alert variant="destructive">
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    La consegna non è disponibile per la zona selezionata (oltre 50 km).
                    Scegli un indirizzo più vicino o seleziona "Ritiro in sede".
                  </AlertDescription>
                </Alert>
                <Button disabled className="w-full mt-4 bg-muted text-muted-foreground" size="lg">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  Checkout non disponibile
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleCheckout}
                disabled={isProcessing || isEmpty}
                className="w-full mt-6 gradient-instagram text-primary-foreground hover:opacity-90 shadow-instagram"
                size="lg"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Elaborazione...
                  </>
                ) : fulfillmentType === 'PICKUP' ? (
                  <>
                    <Store className="w-5 h-5 mr-2" />
                    Paga in sede {formatPrice(total)}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Paga con carta {formatPrice(total)}
                  </>
                )}
              </Button>
            )}

            <p className="text-xs text-center text-muted-foreground mt-3">
              {fulfillmentType === 'PICKUP'
                ? 'Invio ordine diretto al locale. Pagherai al ritiro.'
                : 'Pagamento sicuro con Stripe 🔒'}
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};
